import querystring from "querystring";

import { PROMOTIONS } from "constants/action-types";
import { fetchJSON } from "../../lib/fetch";
import { PriceEntity } from "../../containers/admin/promotions/entity/price-entity/promotions.types";
import { format } from "lib/globalize-util";
import { FetchEntityResult, promotionsService } from "../../containers/admin/promotions/services/promotions";
import type { IPromotionsListFilters } from "containers/admin/promotions/promotions-list/interfaces";
import type { SortState } from "ag-grid-community-32";
import { PROMOTIONS_LIST_KEY } from "containers/admin/promotions/promotions-list";

function fetchData(merchantId: string, pageToken: string) {
    return fetchJSON<{
        next_page_token?: string;
        price_entities: PriceEntity[];
    }>(
        `/api/v1/merchant/${merchantId}/pricing/entities?pageSize=10&folder=UI&includeDisabled=true${
            pageToken ? "&" + querystring.stringify({ page_token: pageToken }) : ""
        }`,
        {
            method: "GET",
            noDomain: true,
        }
    );
}

function fetchFilteredData(merchantId: string, filters: IPromotionsListFilters) {
    const queryParam = {
        ...(filters.type && filters.type.length == 1 && { promotion_type: filters.type[0] }),
        ...(filters.status && filters.status.length == 1 && { enabled: filters.status[0] === "active" }),
        ...(filters.dateRange &&
            filters.dateRange.length == 2 && {
                date_range: `${filters.dateRange[0].toISOString().split("T")[0]},${filters.dateRange[1].toISOString().split("T")[0]}`,
            }),
        ...(filters.createdBy && filters.createdBy.length > 0 && { created_by: filters.createdBy.toString() }),
    };
    const queryParamString = `&${querystring.stringify(queryParam)}`;

    return fetchJSON<{
        price_entities: PriceEntity[];
    }>(
        `/api/v1/merchant/${merchantId}/pricing/entities/filtered?folder=UI${
            Object.keys(queryParam).length > 0 ? queryParamString : ""
        }`,
        {
            method: "GET",
            noDomain: true,
        }
    );
}

export const addPromotion = (duplicate: PriceEntity) => ({
    type: PROMOTIONS.LIST.ADD,
    payload: duplicate,
});

export const fetchCurrentPromotionStart = () => ({
    type: PROMOTIONS.CURRENT.FETCH.START,
});

export const fetchCurrentPromotionError = () => ({
    type: PROMOTIONS.CURRENT.FETCH.ERROR,
});

export const fetchCurrentPromotionSuccess = (entity) => ({
    data: entity,
    type: PROMOTIONS.CURRENT.FETCH.SUCCESS,
});

export const clearCurrentPromotion = () => (dispatch) =>
    dispatch({
        type: PROMOTIONS.CURRENT.CLEAR,
    });

export const fetchCurrentPromotion = (promotionId: string, merchantId: string) => async (dispatch) => {
    dispatch(fetchCurrentPromotionStart());
    try {
        const fetchedThreshold: PriceEntity | null = await promotionsService
            .fetchRelatedThresholdPriceEntity(promotionId, merchantId)
            .catch(() => null);
        const fetched = await fetchJSON<FetchEntityResult>(
            `/api/v1/merchant/${merchantId}/pricing/entities/${promotionId}?folder=UI&includeDisabled=true`,
            {
                method: "GET",
                noDomain: true,
            }
        );
        dispatch(
            fetchCurrentPromotionSuccess({
                uiEntity: fetched.entity,
                thresholdEntity: fetchedThreshold,
            })
        );
    } catch (error) {
        dispatch(fetchCurrentPromotionError());
    }
};

export const deletePromotion = (id: string) => ({
    type: PROMOTIONS.LIST.DELETE,
    payload: { id },
});

export const bulkDeletePromotions = (ids: string[]) => ({
    type: PROMOTIONS.LIST.BULK_DELETE,
    payload: ids,
});

export const bulkEditPromotions = (entities: PriceEntity[]) => ({
    type: PROMOTIONS.LIST.BULK_EDIT,
    payload: {
        results: entities,
    },
});

export const fetchPromotionsStart = (key: string, pageToken: string) => ({
    meta: { key, nextPage: !!pageToken },
    type: PROMOTIONS.LIST.FETCH.START,
});

export const fetchPromotionsSuccess = (results: unknown[], nextPage: string, key: string) => ({
    payload: {
        results,
        nextPage,
    },
    meta: { key },
    type: PROMOTIONS.LIST.FETCH.SUCCESS,
});

export const fetchPromotionsNextPage =
    (results: unknown[], nextPage: string, key: string, merchantId: string) => (dispatch) => {
        dispatch(fetchPromotionsSuccess(results, nextPage, key));

        if (nextPage !== "") {
            dispatch(promotionsListFetch(key, merchantId, nextPage));
        }
    };

export const fetchPromotionsError = (error: string, key: string) => ({
    payload: error,
    meta: { key },
    type: PROMOTIONS.LIST.FETCH.SUCCESS,
});

export const promotionsListFetch = (key: string, merchantId: string, pageToken: string) => (dispatch) => {
    dispatch(fetchPromotionsStart(key, pageToken));
    return fetchData(merchantId, pageToken)
        .then((data) => {
            dispatch(fetchPromotionsNextPage(data.price_entities, data.next_page_token, key, merchantId));
        })
        .catch((error) => {
            fetchPromotionsError(format("Something went wrong"), key);
        });
};

export const setPromotionFilters = (filters: IPromotionsListFilters, results: PriceEntity[]) => ({
    type: PROMOTIONS.LIST.SET_FILTERS,
    payload: {
        filters,
        results,
    },
});

export const setPromotionFetching = (isFetching: boolean) => ({
    type: PROMOTIONS.LIST.SET_FETCHING,
    payload: {
        isFetching,
    },
});

export const setAndApplyPromotionFilters = (filters: IPromotionsListFilters) => ({
    type: PROMOTIONS.LIST.SET_APPLY_FILTERS,
    payload: {
        filters,
    },
});

export const fetchFilteredPromotions =
    (merchantId: string, filters: IPromotionsListFilters, promotionsEnablesElasticSearchFilteringNu64701: boolean) =>
    (dispatch) => {
        // TODO: Clean with promotionsEnablesElasticSearchFilteringNu64701 FF
        if (!promotionsEnablesElasticSearchFilteringNu64701) {
            dispatch(setAndApplyPromotionFilters(filters));
            return;
        }

        if (Object.keys(filters)?.length == 0) {
            dispatch(setPromotionFilters(filters, []));
            return;
        }

        dispatch(setPromotionFetching(true));
        return fetchFilteredData(merchantId, filters)
            .then((data) => {
                dispatch(setPromotionFilters(filters, data.price_entities));
                dispatch(setPromotionFetching(false));
            })
            .catch((error) => {
                fetchPromotionsError(format("Something went wrong"), PROMOTIONS_LIST_KEY);
            });
    };

export const setPromotionsSort = (sort: SortState) => ({
    type: PROMOTIONS.LIST.SET_SORT,
    payload: {
        sort,
    },
});

export const setPromotionsListPersist = (shouldPersist: boolean) => ({
    type: PROMOTIONS.LIST.SET_PERSIST,
    payload: {
        shouldPersist,
    },
});
